import { useEffect, useState } from "react";
import TopNav from "../components/TopNav.jsx";
import ProductMark from "../components/ProductMark.jsx";
import { getToken, isMaster } from "../lib/api";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * LoomPage — Sage's art & image studio · v0.
 * Nano Banana (Gemini 3.1 Flash Image Preview) via Emergent Universal Key.
 * Master-gated weave; public gallery + public Ink Ledger.
 */
export default function LoomPage() {
  const [meter, setMeter] = useState(null);
  const [gallery, setGallery] = useState([]);
  const [prompt, setPrompt] = useState("");
  const [weaving, setWeaving] = useState(false);
  const [lastWeave, setLastWeave] = useState(null);

  const reloadMeter = async () => {
    try {
      const r = await fetch(`${BACKEND_URL}/api/loom/ink-meter`);
      if (r.ok) setMeter(await r.json());
    } catch {}
  };
  const reloadGallery = async () => {
    try {
      const r = await fetch(`${BACKEND_URL}/api/loom/gallery?limit=24`);
      if (r.ok) setGallery((await r.json()).rows || []);
    } catch {}
  };

  useEffect(() => { reloadMeter(); reloadGallery(); }, []);

  const weave = async () => {
    if (!prompt.trim() || prompt.trim().length < 3) {
      toast.error("Prompt too short — at least 3 characters.");
      return;
    }
    setWeaving(true);
    setLastWeave(null);
    try {
      const r = await fetch(`${BACKEND_URL}/api/sage/admin/loom/weave`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getToken()}`,
        },
        body: JSON.stringify({ prompt }),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j?.detail || `HTTP ${r.status}`);
      setLastWeave(j);
      if (j.ok) {
        toast.success(`Woven · $${(j.cost_usd || 0).toFixed(3)} · ${j.latency_ms}ms`);
        setPrompt("");
      } else {
        toast.error(j.error || "The loom returned nothing.");
      }
      reloadMeter();
      reloadGallery();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setWeaving(false);
    }
  };

  const master = isMaster();

  return (
    <div className="min-h-screen bg-sage-bg text-sage-text">
      <TopNav />
      <div className="max-w-6xl mx-auto px-6 py-12">
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2" data-testid="loom-title">Loom</h1>
          <div className="text-sm text-sage-muted leading-relaxed max-w-2xl">
            Sage's art &amp; image studio. Every weave has a cost stamp; the failures are on
            the record too. Master-gated for v0.
          </div>
        </div>

        {/* Ink Meter */}
        <div className="border border-sage-border p-6 mb-8" data-testid="loom-ink-meter">
          <div className="text-xs uppercase tracking-[0.15em] text-sage-muted mb-3">
            The Ink Meter · Loom
          </div>
          {!meter && <div className="text-xs text-sage-faint">Loading…</div>}
          {meter && (
            <div className="grid grid-cols-2 md:grid-cols-5 gap-6">
              <Stat label="Weaves" value={meter.weaves} testid="loom-meter-weaves" />
              <Stat label="Successes" value={meter.successes} testid="loom-meter-successes" />
              <Stat label="Failures" value={meter.failures} testid="loom-meter-failures" />
              <Stat label="$ Burned" value={`$${(meter.cost_usd || 0).toFixed(6)}`} testid="loom-meter-cost" />
              <Stat label="Flat / image" value={`$${(meter.cost_per_image_usd_flat || 0).toFixed(3)}`} testid="loom-meter-flat" />
            </div>
          )}
          {meter && (
            <div className="mt-4 text-[10px] text-sage-faint uppercase tracking-[0.15em]">
              Model: {meter.model} · Fuel: {meter.fuel}
            </div>
          )}
        </div>

        {/* Weave form — master only */}
        {master ? (
          <div className="border border-sage-border p-6 mb-8" data-testid="loom-weave-form">
            <div className="text-xs uppercase tracking-[0.15em] text-sage-muted mb-3">
              Weave a new image
            </div>
            <textarea
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder="Describe the image — style, subject, mood. Be specific; Loom prefers detail."
              className="w-full h-32 bg-sage-input border border-sage-border p-3 text-sm font-mono resize-y focus:outline-none focus:border-sage-accent"
              disabled={weaving}
              data-testid="loom-prompt-input"
            />
            <div className="flex items-center justify-between mt-3">
              <div className="text-[10px] text-sage-faint uppercase tracking-[0.15em]">
                {prompt.length} chars · ~${(0.039).toFixed(3)} per weave
              </div>
              <button
                onClick={weave}
                disabled={weaving || prompt.trim().length < 3}
                className="px-6 py-2 bg-sage-accent text-sage-bg text-xs uppercase tracking-[0.15em] disabled:opacity-40 hover:bg-sage-accent-hover"
                data-testid="loom-weave-btn"
              >
                {weaving ? "Weaving…" : "Weave"}
              </button>
            </div>
            {lastWeave && (
              <div className="mt-4 border border-sage-faint p-3 text-xs" data-testid="loom-last-weave">
                <div className="font-bold mb-1">
                  {lastWeave.ok ? "Woven ↓" : "Failed ↓"}
                </div>
                <div className="text-sage-muted">
                  {lastWeave.ok
                    ? `${lastWeave.bytes?.toLocaleString()} bytes · $${lastWeave.cost_usd?.toFixed(3)} · ${lastWeave.latency_ms}ms`
                    : (lastWeave.error || "unknown error")}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="border border-sage-border p-6 mb-8 text-sm text-sage-muted" data-testid="loom-not-master">
            The loom is master-only for v0. The gallery and meter below are public — every weave lands there with its
            prompt and cost, and every failure lands there too.
          </div>
        )}

        {/* Gallery */}
        <div className="border border-sage-border p-6" data-testid="loom-gallery">
          <div className="text-xs uppercase tracking-[0.15em] text-sage-muted mb-4">
            The Gallery · every weave, wins and losses
          </div>
          {gallery.length === 0 && (
            <div className="text-xs text-sage-faint py-6" data-testid="loom-gallery-empty">
              Nothing woven yet. When Loom fires, every image will land here.
            </div>
          )}
          {gallery.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {gallery.map((row) => (
                <div
                  key={row.weave_id}
                  className="border border-sage-faint p-3 flex flex-col"
                  data-testid={`loom-gallery-row-${row.weave_id}`}
                >
                  {row.ok && row.image_url ? (
                    <a
                      href={`${BACKEND_URL}${row.image_url}`}
                      target="_blank"
                      rel="noreferrer"
                      className="block bg-sage-input mb-2"
                    >
                      <img
                        src={`${BACKEND_URL}${row.image_url}`}
                        alt={row.prompt}
                        loading="lazy"
                        className="w-full h-48 object-cover"
                        data-testid={`loom-image-${row.weave_id}`}
                      />
                    </a>
                  ) : (
                    <div className="w-full h-48 bg-sage-input mb-2 flex items-center justify-center text-xs text-sage-warn uppercase tracking-[0.15em]" data-testid={`loom-failed-${row.weave_id}`}>
                      Failed weave
                    </div>
                  )}
                  <div className="text-xs text-sage-text mb-2 line-clamp-3" title={row.prompt}>
                    {row.prompt}
                  </div>
                  <div className="text-[10px] text-sage-faint uppercase tracking-[0.15em] tabular-nums mt-auto">
                    ${(row.cost_usd || 0).toFixed(3)} · {row.latency_ms}ms · {new Date(row.at).toLocaleString()}
                  </div>
                  {!row.ok && row.error && (
                    <div className="text-[10px] text-sage-warn mt-1 line-clamp-2">
                      {row.error}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, testid }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.15em] text-sage-faint mb-1">{label}</div>
      <div className="text-xl font-bold tabular-nums" data-testid={testid}>{value}</div>
    </div>
  );
}
